import Axios from '@/utils/axios';
import {
  Maintenance,
  MaintenanceStatus,
  MaintenanceSeverity,
} from '@/types/common';
import { ReturnHandoverRequest } from '@/services/api/handovers';

// Request/Response types
export interface CreateMaintenanceRequest {
  reason: string;
  vehicleID: string;
  issueID?: string;
  incidentID?: string;
  categories: string[];
  driverID?: number;
  driverCode?: string;
  driverName?: string;
  clientID?: number;
  clientName?: string;
  oemID?: string;
  issueDescription: string;
  maintenanceDate: string;
  severity: MaintenanceSeverity;
  costResponsibility: string;
  oemServiceCenter: string;
  createdBy?: string;
  estimatedCost: number;
  expectedCompletionDate: string;
  vehicleImageURL?: string;
  notes?: string;
}

export interface UpdateMaintenanceRequest {
  reason?: string;
  vehicleID?: string;
  categories?: string[];
  issueDescription?: string;
  maintenanceDate?: string;
  status?: MaintenanceStatus;
  severity?: MaintenanceSeverity;
  estimatedCost?: number;
  costResponsibility?: string;
  oemServiceCenter?: string;
  expectedCompletionDate?: string;
  vehicleImageURL?: string;
  notes?: string;
  updatedBy?: string;
}

export interface MaintenanceListParams {
  page: number;
  size: number;
  search?: string;
  statuses?: string;
  severities?: string;
  oemIDs?: string;
  modelIDs?: string;
  startDate?: string;
  endDate?: string;
  reasons?: string;
  categories?: string;
  biayaDitanggung?: string;
}

export interface MaintenanceListResponse {
  data: Maintenance[];
  pagination: {
    total: number;
    page: number;
    size: number;
    lastPage: number;
  };
}

// API functions
export const getListMaintenances = async (
  params: MaintenanceListParams
): Promise<MaintenanceListResponse> => {
  const response = await Axios.get('/v1/maintenances', { params });
  return response.data;
};

export const getMaintenanceById = async (id: string): Promise<Maintenance> => {
  const response = await Axios.get(`/v1/maintenances/${id}`);
  return response.data.data || response.data;
};

export interface MaintenanceStatusHistoryMetadata {
  workCompleted?: string | null;
  actualCompletionAt?: string | null;
  actualCost?: number | null;
  completionImageURL?: string | null;
  completionNotes?: string | null;
  costVariance?: string | null;
  costVarianceReason?: string | null;
  isCostSameAsEstimate?: boolean | null;
  decision?: string | null;
  decisionNotes?: string | null;
  permSwapApproved?: boolean | null;
  // Pending approval perm swap
  vehicleData?: {
    vin?: string;
    imei?: string;
    color?: string;
    [key: string]: any;
  } | null;
  draftNewVehicle?: {
    vin?: string;
    imei?: string;
    color?: string;
    [key: string]: any;
  } | null;
  draftReturnVehicle?: {
    returnDate?: string;
    returnNotes?: string;
    [key: string]: any;
  } | null;
  // Pending approval temp swap
  tempOemName?: string | null;
  tempModelName?: string | null;
  tempPlateNumber?: string | null;
  tempSwapGivenAt?: string | null;
  tempSwapNotes?: string | null;
  tempSwapPhotos?: string[] | null;
  tempSwapReturnAt?: string | null;
  permSwapReturnAt?: string | null;
}

export interface MaintenanceStatusHistoryEntry {
  ID: string;
  maintenanceID?: string;
  fromStatus?: MaintenanceStatus | string;
  toStatus: MaintenanceStatus | string;
  metadata?: MaintenanceStatusHistoryMetadata | null;
  createdBy: string;
  createdAt: string;
}

export const getMaintenanceStatusHistory = async (
  id: string
): Promise<MaintenanceStatusHistoryEntry[]> => {
  const response = await Axios.get(`/v1/maintenances/${id}/status-history`);
  const data = response.data.data || response.data;
  return Array.isArray(data) ? data : [];
};

export const createMaintenance = async (
  data: CreateMaintenanceRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.post('/v1/maintenances', data);
  return response.data;
};

export const updateMaintenance = async (
  id: string,
  data: UpdateMaintenanceRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(`/v1/maintenances/${id}`, data);
  return response.data;
};

export const deleteMaintenance = async (id: string): Promise<void> => {
  await Axios.delete(`/maintenances/${id}`);
};

export interface MarkMaintenanceRequest {
  updatedBy: string;
}

export const markMaintenanceAsComplete = async (
  id: string,
  data: MarkMaintenanceRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(
    `/v1/maintenances/${id}/mark-as-complete`,
    data
  );
  return response.data;
};

export interface MarkMaintenanceCompleteRequest {
  workCompleted?: string;
  isCostSameAsEstimate: boolean;
  actualCost: number;
  costVarianceReason?: string;
  actualCompletionAt?: string;
  completionImageURL?: string;
  completionNotes?: string;
  tempSwapReturnAt?: string;
  permSwapReturnAt?: string;
  updatedBy: string;
}

export const markMaintenanceAsCompleteV2 = async (
  id: string,
  data: MarkMaintenanceCompleteRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(
    `/v1/maintenances/${id}/new-mark-as-complete`,
    data
  );
  return response.data;
};

export interface CloseMaintenanceRequest {
  updatedBy: string;
}

export interface StartMaintenanceRequest {
  updatedBy: string;
}

export const closeMaintenanceCase = async (
  id: string,
  data: CloseMaintenanceRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(`/v1/maintenances/${id}/close-case`, data);
  return response.data;
};

export const startMaintenanceWork = async (
  id: string,
  data: StartMaintenanceRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(`/v1/maintenances/${id}/start-work`, data);
  return response.data;
};

export interface MakeMaintenanceDecisionRequest {
  fmDecision: 'APPROVE' | 'REJECT';
  fmDecisionNotes: string;
  updatedBy: string;
}

export const makeMaintenanceDecision = async (
  id: string,
  data: MakeMaintenanceDecisionRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(
    `/v1/maintenances/${id}/make-decision`,
    data
  );
  return response.data;
};

export interface RequestTempSwapRequest {
  tempPlateNumber: string;
  tempModelName: string;
  tempOemName: string;
  tempSwapGivenAt: string;
  tempSwapPhotos: string[];
  tempSwapNotes?: string;
  updatedBy: string;
}

export const requestTempSwap = async (
  id: string,
  data: RequestTempSwapRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(
    `/v1/maintenances/${id}/request-temp-swap`,
    data
  );
  return response.data;
};

export interface RequestPermSwapRequest {
  newVehicleID?: string;
  newVehicle?: {
    platNomor: string;
    vin: string;
    imei?: string;
    modelID: string;
    color: string;
    contractStartDate: string;
    arriveAt: string;
    stnkLink: string;
    vehicleImageURL: string;
    unitEquipment: string[];
    appUsername: string;
    appPassword: string;
  };
  returnVehicle: {
    returnDate: string;
    returnOdometer?: number;
    returnUnitEquipment: string[];
    returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
    returnReason:
      | 'CONTRACT_END'
      | 'IDLE_AT_HUB_TOO_LONG'
      | 'VEHICLE_SWAP'
      | 'CLIENT_REQUEST'
      | 'DUTY_COMPLETED'
      | 'OTHER';
    returnReasonOther?: string;
    returnIssues?: string;
    returnNotes?: string;
    returnBastURL?: string;
    returnOemRepName?: string;
    returnOemRepPhone?: string;
    vehicleReturnImageURL?: string;
  };
  // Bentuknya persis payload return handover, jadi tipenya dipakai langsung
  // alih-alih disalin: salinan sebelumnya sudah menyimpang (tidak punya
  // VEHICLE_SWAP, padahal modal di alur perm-swap justru hanya menawarkan
  // VEHICLE_SWAP dan OTHER).
  returnHandover?: ReturnHandoverRequest;
  updatedBy: string;
}

export const requestPermSwap = async (
  id: string,
  data: RequestPermSwapRequest
): Promise<{ data: Maintenance }> => {
  const response = await Axios.patch(
    `/v1/maintenances/${id}/request-perm-swap`,
    data
  );
  return response.data;
};
